import { EventEmitter, Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { of as throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { AuthenticationService } from "src/app/auth/authentication.service";
import { JobOrdersService } from "src/app/_services/job-orders.service";
import { ChecklistService } from "src/app/_services/checklist.service";
import { PermissionService } from "src/app/auth/permission.service";

@Injectable({
  providedIn: "root",
})
export class AiService {
  refreshList$: EventEmitter<any>;
  refreshStatus$: EventEmitter<any>;
  refreshGoogleCalender$: EventEmitter<any>;
  refreshMicrosoftCalender$: EventEmitter<any>;
  refreshCalendlyCalender$: EventEmitter<any>;

  private ENDPOINT_AI_FS = "/ai-customer-knowledge-base"; // Not In Use(Checked On 1/oct/2024)

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    private perm: PermissionService,
    private authService: AuthenticationService,
    private jobOrderService: JobOrdersService,
    private checklistService: ChecklistService
  ) {
    this.refreshList$ = new EventEmitter();
    this.refreshStatus$ = new EventEmitter();
    this.refreshGoogleCalender$ = new EventEmitter();
    this.refreshMicrosoftCalender$ = new EventEmitter();
    this.refreshCalendlyCalender$ = new EventEmitter();
  }

  // **************************************  Method to get job order ID *************************************/
  getJobOrderId(jobOrderId: string){
    return this.jobOrderService.getJobOrderProductsById(jobOrderId);
  }

  // **************************************  Get AI Summary *************************************/
  public getConnectWithAI(fromDateTime: any, toDateTime: any) {
    const params = new HttpParams()
      .set("fromDateTime", fromDateTime)
      .set("toDateTime", toDateTime)
      .set("outputLang", "en")
      .set("useTrial", "True");

    return this.http
      .get(
        environment.ai_base_url + "/ai-daily-rundown/planner-daily-rundown",
        { observe: "response", params }
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(error))
      );
  }

  // **************************************  Get Predicted AI Completion Time *************************************/
  public getPredictedCompletionTime(
    tenantId: string,
    jobOrderId: string
  ){
    const params = new HttpParams()
      .set("TenantId", tenantId)
      .set("JobOrderId", jobOrderId);

    console.log("Requesting API with params:", params);

    return this.http
      .get(environment.ai_base_url + "/ai-predict-completion-time/invocations", {
        observe: "response",
        params,
      })
      .pipe(
        map((response: any) => response),
        catchError((error) => throwError(error))
      );
  }

  // **************************************  Get AI Job Order Summary *************************************/
  getAIJobOrderSummary(jobOrderId: string, lang: any){
    const params = new HttpParams()
      .set("jobOrderId", jobOrderId)
      .set("outputLang", lang)
      .set("useTrial", "True");

    return this.http
      .get(
        environment.ai_base_url + "/ai-daily-rundown/planner-post-job-summary",
        { params }
      )
      .pipe(
        map((response: any) => response),
        catchError((error) => {
          console.error("Error fetching job order summary:", error);
          // must return throwError, not []
          return throwError([]);
        })
      );
  }

  // **************************************  Generate AI Checklist *************************************/
  public GenerateAiChecklist(checklistData: any){
    const url =
      environment.ai_base_url + "/ai-generate-checklists/generate-checklist";

    const formData = new FormData();
    formData.append("userId", checklistData.userId);
    formData.append("checklistDescription", checklistData.checklistDescription);
    formData.append("outputLang", "en");

    if (checklistData.referenceFile) {
      formData.append(
        "file",
        checklistData.referenceFile,
        checklistData.referenceFile.name
      );
    }

    // HttpHeaders is immutable -> use set()
    let headers = new HttpHeaders();
    // If you were trying to send an "origin" header, browsers block it.
    // Keep it removed to avoid silent failures.

    return this.http
      .post(url, formData, {
        headers,
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Post AI Checklist Questions *************************************/
  public postChecklistQuestions(body: any) {
    return this.http
      .post(
        environment.ms_base_url + "/v1/tmpl-checklist-questions/bulk",
        body,
        { observe: "response" }
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  //**************************************  Get AI Organization Details *************************************/
  getAIOrganizationFeatures(tenantId: any){
    return this.http
      .get(environment.base_url + "/get-org-ai-features", {
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Update AI Organization Features *************************************/
  updateAIOrganizationFeatures(body: any){
    return this.http
      .put(environment.base_url + "/update-org-ai-features", body, {
        observe: "response",
      })
      .pipe(
        map((resp: any) => {
          console.log(resp);
          return resp;
        }),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Upload Finley Suggestion Doc *************************************/
  public uploadAttachmentAiDoc(formData: any) {
    // Replace @angular/http upload with HttpClient upload
    const token = this.auth.getToken?.();
    let headers = new HttpHeaders();
    if (token) headers = headers.set("Authorization", "Bearer " + token);

    return this.http
      .post(environment.ai_base_url + this.ENDPOINT_AI_FS + "/upload", formData, {
        headers,
        observe: "response",
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Get List of uploaded Documents *************************************/
  getListOfUploadedDocuments(tenantId: any){
    return this.http
      .get(
        environment.ai_base_url +
          this.ENDPOINT_AI_FS +
          "/list-knowledge-base?tenantId=" +
          tenantId,
        { observe: "response" }
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Delete Attachments *************************************/
  public deleteAIAttchments(tenantId: any, fileName: any) {
    const params = new HttpParams().set("fileName", fileName).set("tenantId", tenantId);

    return this.http
      .delete(environment.ai_base_url + this.ENDPOINT_AI_FS + "/delete", {
        observe: "response",
        params,
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Get JO => Notes *************************************/
  getSmartSuggestForJo(body: any){
    console.log("Requesting API with params:", body);
    return this.http
      .post(
        environment.ai_base_url +
          this.ENDPOINT_AI_FS +
          "/generate-resolution-steps",
        body,
        { observe: "response" }
      )
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }

  //**************************************  Get Products based on JO *************************************/
  getProductsBaseOnJo(tenantId: any, jobOrderId: any){
    const params = new HttpParams()
      .set("jobOrderId", jobOrderId)
      .set("tenantId", tenantId);

    return this.http
      .get(environment.ai_base_url + this.ENDPOINT_AI_FS + "/get-spare-parts", {
        observe: "response",
        params,
      })
      .pipe(
        map((resp: any) => resp),
        catchError((error) => throwError(() => error))
      );
  }
}
